/**
 * Slide document types for the course renderer (CEE-896).
 *
 * A LOCAL MIRROR of the interaction registry in
 * `infrastructure/lambda/shared/src/interactions/`. It is duplicated rather than imported
 * because that lives in a separate npm root with no workspace linking it here, and the
 * renderer must not depend on zod (see ../README.md).
 *
 * Keep the layout ids and payload shapes in step with the registry. The generator's zod
 * schemas stay authoritative — everything here is deliberately permissive, because a
 * renderer that rejects content shows a learner a blank slide.
 */

/** The layout ids from the Figma "Standard slide types" board, plus the fallback. */
export type SlideLayoutId =
  | 'title_slide'
  | 'text_and_picture'
  | 'text_and_background'
  | 'carousel_full_page'
  | 'carousel_half'
  | 'radio_two_column'
  | 'flip_cards'
  | 'layer_cards'
  | 'accordion_vertical'
  | 'accordion_horizontal'
  | 'hotspot_full_width'
  | 'hotspot_in_column'
  | 'checklist'
  | 'sorting'
  | 'index_slide'
  | 'unclassified';

/** Pre-rename layout ids still present in older stored documents. */
const LEGACY_LAYOUT_IDS: Readonly<Record<string, SlideLayoutId>> = {
  two_column: 'text_and_picture',
  hotspot_full_page: 'hotspot_full_width',
};

/** Rewrite a stored layout id to the current vocabulary. */
export function normalizeLayoutId(value: string | undefined): SlideLayoutId | undefined {
  if (!value) return undefined;
  if (value in LEGACY_LAYOUT_IDS) return LEGACY_LAYOUT_IDS[value];
  return value as SlideLayoutId;
}

/** Static slide chrome. Keys are free-form in the schema; these are the ones generated today. */
export interface ZoneContent {
  text?: string;
  imageKey?: string;
  imageAlt?: string;
}

export type Zones = Partial<
  Record<'heading' | 'subheading' | 'body' | 'instruction' | 'intro' | 'design_note', ZoneContent>
> &
  Record<string, ZoneContent | undefined>;

export interface CarouselItem {
  id: string;
  heading?: string;
  body: string;
  imageKey?: string;
  imageAlt?: string;
}

export interface RevealItem {
  id: string;
  /** Absent on the `icon_only` flip card, whose face is the glyph alone. */
  label?: string;
  body: string;
  imageKey?: string;
  imageAlt?: string;
  icon?: string;
}

export interface RadioOption {
  id: string;
  text: string;
  feedback?: string;
}

export interface ChecklistItem {
  id: string;
  body: string;
}

export interface SortingBucket {
  id: string;
  label: string;
}

export interface SortingItem {
  id: string;
  text: string;
  /** The bucket this item belongs in — the answer key. Never shown to the learner. */
  bucketId: string;
  feedback?: string;
}

export interface HotspotItem {
  id: string;
  label: string;
  body: string;
  /** Percentage of the canvas, 0-100. Absent when the author has not placed the marker. */
  x?: number;
  y?: number;
  /** Material Symbols ligature drawn in the detail card, same convention as RevealItem.icon. */
  icon?: string;
}

/** One module card on the CoC / multi-module hub (`index_slide`). */
export interface IndexItem {
  id: string;
  title: string;
  /** Material Symbols ligature drawn in the blue disc. */
  icon?: string;
}

interface ScoredFeedback {
  correctFeedback?: string;
  incorrectFeedback?: string;
}

/**
 * One interface per family, each covering its family's member layout ids. A family's `type` is a
 * union of exactly its `memberIds`, mirroring `interactionTypeSchema` in the registry — which is
 * what makes `interaction.type !== slide.layout` a meaningful inconsistency check.
 */
export interface CarouselInteraction {
  type: 'carousel_full_page' | 'carousel_half';
  items: CarouselItem[];
}

export interface RevealPairsInteraction {
  type: 'flip_cards' | 'layer_cards' | 'accordion_vertical' | 'accordion_horizontal';
  items: RevealItem[];
}

export interface RadioInteraction extends ScoredFeedback {
  type: 'radio_two_column';
  prompt: string;
  options: RadioOption[];
  correctOptionId: string;
}

export interface ChecklistInteraction {
  type: 'checklist';
  items: ChecklistItem[];
}

export interface SortingInteraction extends ScoredFeedback {
  type: 'sorting';
  buckets: SortingBucket[];
  items: SortingItem[];
}

export interface HotspotInteraction {
  type: 'hotspot_full_width' | 'hotspot_in_column';
  imageKey: string;
  imageAlt: string;
  items: HotspotItem[];
}

export interface IndexInteraction {
  type: 'index_slide';
  items: IndexItem[];
}

export type SlideInteraction =
  | CarouselInteraction
  | RevealPairsInteraction
  | RadioInteraction
  | ChecklistInteraction
  | SortingInteraction
  | HotspotInteraction
  | IndexInteraction;

export interface ContentSlide {
  slide_number: number;
  title?: string;
  confidence?: number;
  locked?: boolean;
  layout: SlideLayoutId;
  variant?: string;
  zones?: Zones;
  interaction?: SlideInteraction;
  source?: {
    images?: string[];
    tables?: string[][][];
    parserInteractionType?: string;
  };
  /**
   * Generated xAPI statement template. Opaque here on purpose: the Runtime consumes it when
   * writing tracking, and the Renderer must never read a `verb` or an activity id. Typed only
   * so it survives a round trip through the editor.
   */
  xapi_statement?: Record<string, unknown>;
  /**
   * The `coc_module` template this slide was copied from, when the deck is a composed CoC
   * training (CEE-734). Absent on a standalone deck, and absent on the training's own index
   * slide — which is exactly what makes it the provenance signal: a slide that carries one was
   * authored somewhere else, so the editor renders it read-only and the API refuses to store
   * an edit to it.
   *
   * Nothing about RENDERING branches on it — the same slide draws the same way in its module
   * and in the training. Typed here so it survives a round trip through the editor.
   */
  module_id?: string;
}

export interface ContentDocument {
  schema_version?: number;
  total_slides?: number;
  slides: ContentSlide[];
  /**
   * Per-course brand color tokens (CEE-1061). Absent for templates, which have no branding
   * concept server-side. Read by the SCORM export brand-CSS builder and by the authoring
   * editor's Color tokens panel — the renderer itself never reads this.
   */
  branding?: { theme?: Record<string, string> };
}

/** Which payload component draws a layout id. Mirrors the registry's family assignment. */
export const FAMILY_BY_LAYOUT: Record<SlideLayoutId, string> = {
  title_slide: 'pure_zone',
  text_and_picture: 'pure_zone',
  text_and_background: 'pure_zone',
  carousel_full_page: 'carousel',
  carousel_half: 'carousel',
  radio_two_column: 'radio',
  flip_cards: 'reveal_pairs',
  layer_cards: 'reveal_pairs',
  accordion_vertical: 'reveal_pairs',
  accordion_horizontal: 'reveal_pairs',
  hotspot_full_width: 'hotspot',
  hotspot_in_column: 'hotspot',
  checklist: 'checklist',
  sorting: 'sorting',
  index_slide: 'index',
  unclassified: 'unclassified',
};

/**
 * Layouts that put the slide's media in a column beside the interaction rather than
 * across the full canvas. Drives the two-column split in SlideFrame.
 */
export const SPLIT_LAYOUTS: ReadonlySet<SlideLayoutId> = new Set<SlideLayoutId>([
  'text_and_picture',
  'carousel_half',
  'radio_two_column',
  'checklist',
  'sorting',
]);
